import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { useCheckVerifyEmail, useSendVerifyEmail, useSignUp } from '../../query/auth';
import { useGetDepartments } from '../../query/department';
import { useGetSchools } from '../../query/school';
import CheckboxField from '../fields/checkbox-field';
import InputField from '../fields/input-field';
import SelectField from '../fields/select-field';
import TermsOfService from '../term-of-condition';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Drawer, DrawerContent, DrawerHeader } from '../ui/drawer';
import { Form } from '../ui/form';

const formSchema = z
  .object({
    email: z
      .string({ required_error: '이메일을 입력해주세요.' })
      .email('유효한 이메일 주소를 입력해주세요.'),
    token: z.string({ required_error: '인증번호를 입력해주세요.' }),
    password: z
      .string({ required_error: '비밀번호를 입력해주세요.' })
      .min(6, '비밀번호는 6자 이상이어야 합니다.'),
    rePassword: z
      .string({ required_error: '비밀번호를 입력해주세요.' })
      .min(6, '비밀번호 재입력은 필수입니다.'),
    studentNumber: z
      .string({ required_error: '학번을 입력해주세요.' })
      .min(6, '학번을 입력해주세요.'),

    school: z.string({ required_error: '학교를 선택해주세요.' }),
    department: z.string({ required_error: '학과를 선택해주세요.' }),
    departmentName: z.string().optional(),

    name: z.string({ required_error: '이름을 입력해주세요.' }).min(1, '이름을 입력해주세요.'),

    isTermsAppove: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.password !== data.rePassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '비밀번호가 일치하지 않습니다.',
        path: ['rePassword'],
      });
    }
    if (
      data.department === '직접입력' &&
      (!data.departmentName || data.departmentName.trim() === '')
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '학과명을 입력해주세요.',
        path: ['departmentName'],
      });
    }
    if (!data.isTermsAppove) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '개인정보 수집 및 이용동의 해주세요.',
        path: ['isTermsAppove'],
      });
    }
  });

type FormData = z.infer<typeof formSchema>;

const SignUpForm = () => {
  const navigate = useNavigate();
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      rePassword: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const [emailVerified, setEmailVerified] = useState<undefined | boolean>(undefined);
  const { data: schools } = useGetSchools();
  const { data: departments } = useGetDepartments();
  const { mutate: sendVerifyEmail, isPending: isSendingVerifyEmail } = useSendVerifyEmail();
  const { mutate: checkVerifyEmail, isPending: isCheckingVerifyEmail } = useCheckVerifyEmail();

  const [isOpen, setIsOpen] = useState(false);

  const { mutate: signUp, isPending: isSigningUp } = useSignUp();

  const onSubmit = (data: FormData) => {
    signUp(
      {
        ...data,
        department: data.department === '직접입력' ? (data.departmentName ?? '') : data.department,
      },
      {
        onSuccess: () => {
          toast.success('회원가입을 완료했습니다.');
          navigate('/sign-in', { replace: true });
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          toast.error(error?.message ?? '회원가입에 실패했습니다.');
        },
      },
    );
  };

  return (
    <Card className="w-[500px]">
      <CardHeader>
        <CardTitle>학생 회원가입</CardTitle>
        <CardDescription>회원가입 해주세요.</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <InputField
                  control={form.control}
                  name="email"
                  placeholder="이메일을 입력해주세요."
                  label="이메일"
                  type="email"
                  autoComplete="username"
                />
              </div>
              <Button
                type="button"
                onClick={async () => {
                  const isValid = await form.trigger('email');
                  if (!isValid) return;
                  sendVerifyEmail(
                    { email: form.getValues('email') },
                    {
                      onSuccess: () => {
                        setEmailVerified(false);
                      },
                    },
                  );
                }}
                disabled={emailVerified != undefined || isSendingVerifyEmail}
              >
                이메일 인증
              </Button>
            </div>

            {emailVerified != undefined && (
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <InputField
                    control={form.control}
                    name="token"
                    placeholder="인증번호를 입력해주세요."
                    label="인증번호"
                    type="text"
                    disabled={emailVerified === true}
                  />
                </div>
                <Button
                  type="button"
                  onClick={() => {
                    const token = form.watch('token');
                    if (!token) {
                      form.setError('token', { message: '인증번호를 입력해주세요.' });
                      return;
                    }
                    checkVerifyEmail(
                      {
                        email: form.watch('email'),
                        token,
                      },
                      {
                        onSuccess: () => {
                          setEmailVerified(true);
                        },
                        onError: () => {
                          toast.error('인증번호가 유효하지 않습니다.');
                        },
                      },
                    );
                  }}
                  disabled={emailVerified === true || isCheckingVerifyEmail}
                >
                  인증번호 확인
                </Button>
              </div>
            )}

            <InputField
              control={form.control}
              name="password"
              placeholder="비밀번호를 입력해주세요."
              label="비밀번호"
              type="password"
              autoComplete="current-password"
            />
            <InputField
              control={form.control}
              name="rePassword"
              placeholder="비밀번호를 다시 입력해주세요."
              label="비밀번호 재입력"
              type="password"
            />

            <SelectField
              control={form.control}
              name="school"
              placeholder="학교를 선택해주세요."
              label="학교"
              options={
                schools?.data?.map(school => ({
                  label: school.name,
                  value: school.name,
                })) ?? []
              }
            />
            <SelectField
              control={form.control}
              name="department"
              placeholder="학과를 선택해주세요."
              label="학과"
              options={[
                ...(departments?.data?.map(department => ({
                  label: department.name,
                  value: department.name,
                })) ?? []),
                { label: '직접입력', value: '직접입력' },
              ]}
            />
            {form.watch('department') === '직접입력' && (
              <InputField
                control={form.control}
                name="departmentName"
                placeholder="학과를 입력해주세요."
                label="학과 직접입력"
                type="text"
              />
            )}

            <InputField
              control={form.control}
              name="studentNumber"
              placeholder="학번을 입력해주세요."
              label="학번"
              type="text"
            />
            <InputField
              control={form.control}
              name="name"
              placeholder="이름을 입력해주세요."
              label="이름"
              type="text"
            />

            <div className="flex gap-4 items-center justify-between">
              <CheckboxField
                control={form.control}
                name={'isTermsAppove'}
                label={'개인정보 수집 및 이용동의(필수)'}
              />

              <Button
                type="button"
                variant={'ghost'}
                onClick={() => {
                  setIsOpen(true);
                }}
              >
                <ChevronRight width={20} height={20} className="!w-[20px] !h-[20px]" />
              </Button>
            </div>

            <Drawer
              open={isOpen}
              onOpenChange={open => {
                setIsOpen(open);
              }}
            >
              <DrawerContent className="h-screen flex justify-center items-center">
                <DrawerHeader className="h-[40px] flex justify-end max-w-4xl w-full mb-6">
                  <Button
                    type="button"
                    onClick={() => {
                      setIsOpen(false);
                    }}
                  >
                    닫기
                  </Button>
                </DrawerHeader>
                <TermsOfService />
              </DrawerContent>
            </Drawer>

            <div className="flex w-full items-center justify-center gap-4">
              <Button type="submit" className="align-middle" disabled={isSigningUp}>
                회원가입
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
      <CardFooter>
        <div className="w-full flex justify-center items-center">
          <Button variant="link" asChild>
            <Link to={'/sign-in'}>로그인하기</Link>
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default SignUpForm;
